import { useTranslation } from 'react-i18next';
import Table from '@shared/components/Table';
import { useState, useEffect } from 'react';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import { companiesService } from '@shared/services/companiesService';
import { personService } from '@shared/services/personService';
import { additionalAccessService } from '../services/additionalAccess';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import DeleteModal from '@shared/components/DeleteModal';

export default function AdditionalAccess() {
    const { t } = useTranslation();
    const [selectedUserRecord, setSelectedUserRecord] = useState(null);
    const [selectedRecord2, setSelectedRecord2] = useState(null);
    const [loading, setLoading] = useState(false);
    const [nomstructLoading, setNomstructLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState({});
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [selectedActive, setSelectedActive] = useState(null);
    const [personsData, setPersonsData] = useState([]);
    const [searchType, setSearchType] = useState(null);
    const [branchesData, setBranchesData] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [selectedAdministerBranch, setSelectedAdministerBranch] = useState(null);
    const [selectedReportsBranches, setSelectedReportsBranches] = useState([]); // Array for multiple selections
    const [accessRightsData, setAccessRightsData] = useState([]);
    const [profileDetails, setProfileDetails] = useState(null);
    const [calendarChecked, setCalendarChecked] = useState(false);
    const [normsChecked, setNormsChecked] = useState(false);
    const [fixChecked, setFixChecked] = useState(false);
    const [editReportTemplatesChecked, setEditReportTemplatesChecked] = useState(false);
    const [profileName, setProfileName] = useState('');
    const [profileDescription, setProfileDescription] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const accesRightsColumns = [
        {
            key: 'activeProfile',
            label: t('additionalSettings.additionalAccess.table.headers.activeProfile'),
            render: (item) => {
                return item?.activeProfile === true ? t('common.yes') : t('common.no');
            }
        },
        {
            key: 'titleProfile',
            label: t('additionalSettings.additionalAccess.table.headers.titleProfile'),
        },
        {
            key: 'descriptionProfile',
            label: t('additionalSettings.additionalAccess.table.headers.descriptionProfile'),
        },
    ]

    const usersColumns = [
        {
            key: 'personFullName',
            label: t('common.name'),
            render: (item) => {
                // Then check direct access (normal case when item is from array)
                return item?.content?.personFullName || item?.personFullName || '';
            }
        },
        {
            key: 'empId',
            label: t('common.number'),

        },
        {
            key: 'username',
            label: t('common.user'),
        },
    ]
    const actionButtons = (
        <ActionButtons
            buttons={[
                ...(!isEditing ? [
                    {
                        ...COMMON_BUTTONS.ADD,
                        onClick: () => addHandler(),
                        showWhen: 'always'
                    },
                    {
                        ...COMMON_BUTTONS.EDIT,
                        onClick: () => editHandler(),
                        showWhen: 'whenSelected'
                    },
                    {
                        ...COMMON_BUTTONS.DELETE,
                        onClick: () => deleteHandler(),
                        showWhen: 'whenSelected'
                    },
                    {
                        ...COMMON_BUTTONS.CANCEL,
                        onClick: () => cancelHandler(),
                        showWhen: 'whenSelected'
                    }
                ] : [
                    {
                        ...COMMON_BUTTONS.SAVE,
                        onClick: () => saveHandler(),
                        showWhen: 'always'
                    },
                    {
                        ...COMMON_BUTTONS.CANCEL,
                        onClick: () => cancelHandler(),
                        showWhen: 'always'
                    }
                ])
            ]}
            selectedRecord={selectedRecord2}
        />
    );

    const searchOptions = [
        { key: 'fullName', label: 'name' },
        { key: 'empId', label: 'number' },
        { key: 'username', label: 'user' },
    ]

    // Fetch companies data on component mount
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                setBranchesLoading(true);
                const companies = await companiesService.getAllCompanies();
                setBranchesData(companies || []);
            } catch (error) {
                console.error('Error fetching companies:', error);
                setBranchesData([]);
            } finally {
                setBranchesLoading(false);
            }
        };

        fetchBranches();
    }, []);

    // Fetch persons data
    const fetchPersons = async () => {
        try {
            setLoading(true);
            // Convert 1-based pageNumber to 0-based for API
            const apiPageNumber = pageNumber - 1;
            
            // Prepare search query - filter out empty values
            let searchParams = {};
            if (searchQuery && typeof searchQuery === 'object') {
                // Filter out empty values from searchQuery object
                Object.entries(searchQuery).forEach(([key, value]) => {
                    if (value && typeof value === 'string' && value.trim()) {
                        searchParams[key] = value.trim();
                    }
                });
            } else if (searchQuery && typeof searchQuery === 'string') {
                // Legacy string support - use searchType if available, otherwise default to fullName
                if (searchType) {
                    searchParams[searchType] = searchQuery.trim();
                } else {
                    searchParams.fullName = searchQuery.trim();
                }
            }

            const response = await personService.getExtendedAccessPersons({
                searchQuery: searchParams,
                searchType: searchType,
                pageNumber: apiPageNumber,
                pageSize: pageSize,
                sortBy: 'username',
                sortDir: 'asc'
            });

            setPersonsData(response.content || []);
            setTotalPages(response.totalPages || 0);
            setTotalElements(response.totalElements || 0);
            // Update pageNumber if API returned a different page number
            if (response.pageNumber !== undefined && response.pageNumber + 1 !== pageNumber) {
                setPageNumber(response.pageNumber + 1);
            }
        } catch (error) {
            console.error('Error fetching persons:', error);
            setPersonsData([]);
            setTotalPages(0);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    };

    // Fetch persons on component mount and when pagination/search changes
    useEffect(() => {
        fetchPersons();
    }, [pageNumber, pageSize, selectedActive]);

    // Handle search click
    const handleSearchClick = (getDataFunc, params = {}) => {
        setPageNumber(1); // Reset to first page when searching
        fetchPersons();
    };

    // Handle sort (if needed)
    const handleSort = () => {
        fetchPersons();
    };

    // Handle checkbox change for person selection
    const handlePersonCheckboxChange = async (index) => {
        if (index >= 0 && index < personsData.length) {
            const record = personsData[index];
            const newSelection = selectedUserRecord?.id === record.id ? null : record;
            setSelectedUserRecord(newSelection);
            
            if (newSelection?.id) {
                try {
                    setNomstructLoading(true);
                    // Fetch both methods when user is selected
                    const [profilesData, profileDetailsData] = await Promise.all([
                        additionalAccessService.getHrProfilesUsersByUserId(newSelection.id),
                    ]);
                    
                    // Map profiles data to match table columns structure
                    const mappedProfiles = Array.isArray(profilesData) ? profilesData.map(profile => ({
                        id: profile.id,
                        activeProfile: profile.default,
                        titleProfile: profile.shortDescription,
                        descriptionProfile: profile.longDescription
                    })) : [];
                    
                    setAccessRightsData(mappedProfiles);
                    setProfileDetails(profileDetailsData);
                    
                    // Populate form fields from profileDetails
                    if (profileDetailsData) {
                        setCalendarChecked(profileDetailsData.calendar === true);
                        setNormsChecked(profileDetailsData.norms === true);
                        setFixChecked(profileDetailsData.fix === true);
                        setEditReportTemplatesChecked(profileDetailsData.editReportTemplates === true);
                        setProfileName(profileDetailsData.shortDescription || '');
                        setProfileDescription(profileDetailsData.longDescription || '');
                        setSelectedAdministerBranch(profileDetailsData.branchIdForAdmins || null);
                        setSelectedReportsBranches(Array.isArray(profileDetailsData.branchIdsForReports) ? profileDetailsData.branchIdsForReports : []);
                    }
                    
                    // Reset selected record from access rights table
                    setSelectedRecord2(null);
                    setIsEditing(false);
                    setIsAdding(false);
                } catch (error) {
                    console.error('Error fetching user profiles:', error);
                    setAccessRightsData([]);
                    setProfileDetails(null);
                    // Clear form fields
                    setCalendarChecked(false);
                    setNormsChecked(false);
                    setFixChecked(false);
                    setEditReportTemplatesChecked(false);
                    setProfileName('');
                    setProfileDescription('');
                    setSelectedAdministerBranch(null);
                    setSelectedReportsBranches([]);
                    setIsEditing(false);
                    setIsAdding(false);
                } finally {
                    setNomstructLoading(false);
                }
            } else {
                // Clear data if no record selected
                setAccessRightsData([]);
                setProfileDetails(null);
                setSelectedRecord2(null);
                // Clear form fields
                setCalendarChecked(false);
                setNormsChecked(false);
                setFixChecked(false);
                setEditReportTemplatesChecked(false);
                setProfileName('');
                setProfileDescription('');
                setSelectedAdministerBranch(null);
                setSelectedReportsBranches([]);
                setIsEditing(false);
                setIsAdding(false);
            }
        }
    };

    // Handle checkbox change for access rights table selection
    const handleAccessRightsCheckboxChange = async (index) => {
        if (index >= 0 && index < accessRightsData.length) {
            const record = accessRightsData[index];
            const newSelection = selectedRecord2?.id === record.id ? null : record;
            setSelectedRecord2(newSelection);
            
            if (newSelection?.id && selectedUserRecord?.id) {
                try {
                    setNomstructLoading(true);
                    // Fetch only the second method when access rights record is selected
                    const profileDetailsData = await additionalAccessService.getHrProfilesUsers(newSelection.id);
                    setProfileDetails(profileDetailsData);
                    
                    // Populate form fields from profileDetails
                    if (profileDetailsData) {
                        setCalendarChecked(profileDetailsData.calendar === true);
                        setNormsChecked(profileDetailsData.norms === true);
                        setFixChecked(profileDetailsData.fix === true);
                        setEditReportTemplatesChecked(profileDetailsData.editReportTemplates === true);
                        setProfileName(profileDetailsData.shortDescription || '');
                        setProfileDescription(profileDetailsData.longDescription || '');
                        setSelectedAdministerBranch(profileDetailsData.branchIdForAdmins || null);
                        setSelectedReportsBranches(Array.isArray(profileDetailsData.branchIdsForReports) ? profileDetailsData.branchIdsForReports : []);
                    }
                    // Reset edit mode when selecting a new record
                    setIsEditing(false);
                    setIsAdding(false);
                } catch (error) {
                    console.error('Error fetching profile details:', error);
                    setProfileDetails(null);
                    // Clear form fields
                    setCalendarChecked(false);
                    setNormsChecked(false);
                    setFixChecked(false);
                    setEditReportTemplatesChecked(false);
                    setProfileName('');
                    setProfileDescription('');
                    setSelectedAdministerBranch(null);
                    setSelectedReportsBranches([]);
                    setIsEditing(false);
                    setIsAdding(false);
                } finally {
                    setNomstructLoading(false);
                }
            } else {
                setProfileDetails(null);
                // Clear form fields
                setCalendarChecked(false);
                setNormsChecked(false);
                setFixChecked(false);
                setEditReportTemplatesChecked(false);
                setProfileName('');
                setProfileDescription('');
                setSelectedAdministerBranch(null);
                setSelectedReportsBranches([]);
                setIsEditing(false);
                setIsAdding(false);
            }
        }
    };

    // Handler functions for action buttons
    const editHandler = () => {
        setIsEditing(true);
    };

    const cancelHandler = () => {
        setIsEditing(false);
        setIsAdding(false);
        // Reset form fields to original profileDetails values or clear them
        if (profileDetails && !isAdding) {
            setCalendarChecked(profileDetails.calendar === true);
            setNormsChecked(profileDetails.norms === true);
            setFixChecked(profileDetails.fix === true);
            setEditReportTemplatesChecked(profileDetails.editReportTemplates === true);
            setProfileName(profileDetails.shortDescription || '');
            setProfileDescription(profileDetails.longDescription || '');
            setSelectedAdministerBranch(profileDetails.branchIdForAdmins || null);
            setSelectedReportsBranches(Array.isArray(profileDetails.branchIdsForReports) ? profileDetails.branchIdsForReports : []);
        } else {
            // Clear all fields if in add mode
            setCalendarChecked(false);
            setNormsChecked(false);
            setFixChecked(false);
            setEditReportTemplatesChecked(false);
            setProfileName('');
            setProfileDescription('');
            setSelectedAdministerBranch(null);
            setSelectedReportsBranches([]);
        }
    };

    const saveHandler = async () => {
        // Validate user is selected
        if (!selectedUserRecord?.id) {
            notifyError(t('common.selectUser') || 'Моля изберете потребител');
            return;
        }

        // Validate profile name is provided
        if (!profileName || !profileName.trim()) {
            notifyError(t('validations.profileNameRequired') || 'Моля въведете име на профила');
            return;
        }

        try {
            setNomstructLoading(true);
            
            // Prepare the request body
            const requestBody = {
                userId: selectedUserRecord.id,
                shortDescription: profileName.trim(),
                longDescription: profileDescription.trim(),
                branchIdsForReports: selectedReportsBranches,
                branchIdForAdmins: selectedAdministerBranch,
                calendar: calendarChecked,
                fix: fixChecked,
                norms: normsChecked,
                editReportTemplates: editReportTemplatesChecked
            };

            if (isAdding) {
                // Create new profile
                const newProfile = await additionalAccessService.createHrProfilesUsers(requestBody);
                
                // Refresh the access rights table data
                if (selectedUserRecord?.id) {
                    const profilesData = await additionalAccessService.getHrProfilesUsersByUserId(selectedUserRecord.id);
                    const mappedProfiles = Array.isArray(profilesData) ? profilesData.map(profile => ({
                        id: profile.id,
                        activeProfile: profile.default,
                        titleProfile: profile.shortDescription,
                        descriptionProfile: profile.longDescription
                    })) : [];
                    setAccessRightsData(mappedProfiles);
                    
                    // Select the newly created profile
                    if (newProfile?.id) {
                        const newProfileInTable = mappedProfiles.find(p => p.id === newProfile.id);
                        if (newProfileInTable) {
                            const index = mappedProfiles.findIndex(p => p.id === newProfile.id);
                            if (index >= 0) {
                                await handleAccessRightsCheckboxChange(index);
                            }
                        }
                    }
                }
                
                setIsAdding(false);
                notifySuccess(t('common.createdSuccessfully'));
            } else {
                // Update existing profile
                if (!selectedRecord2?.id) {
                    notifyError(t('common.noRecordSelected'));
                    return;
                }

                requestBody.id = selectedRecord2.id;
               

                await additionalAccessService.updateHrProfilesUsers(selectedRecord2.id, requestBody);
                
                // Refresh the profile details
                const updatedProfileDetails = await additionalAccessService.getHrProfilesUsers(selectedRecord2.id);
                setProfileDetails(updatedProfileDetails);
                
                // Update the access rights table data
                if (selectedUserRecord?.id) {
                    const profilesData = await additionalAccessService.getHrProfilesUsersByUserId(selectedUserRecord.id);
                    const mappedProfiles = Array.isArray(profilesData) ? profilesData.map(profile => ({
                        id: profile.id,
                        activeProfile: profile.default,
                        titleProfile: profile.shortDescription,
                        descriptionProfile: profile.longDescription
                    })) : [];
                    setAccessRightsData(mappedProfiles);
                }
                
                setIsEditing(false);
                notifySuccess(t('common.updatedSuccessfully'));
            }
        } catch (error) {
            console.error('Error saving profile:', error);
            const errorMessage = error?.response?.data?.message || t('common.saveError') || 'Грешка при запазване';
            notifyError(errorMessage);
        } finally {
            setNomstructLoading(false);
        }
    };

    const addHandler = () => {
        // Check if user is selected
        if (!selectedUserRecord?.id) {
            notifyError(t('common.selectUser') || 'Моля изберете потребител първо');
            return;
        }

        // Clear all form fields
        setCalendarChecked(false);
        setNormsChecked(false);
        setFixChecked(false);
        setEditReportTemplatesChecked(false);
        setProfileName('');
        setProfileDescription('');
        setSelectedAdministerBranch(null);
        setSelectedReportsBranches([]);
        setProfileDetails(null);
        setSelectedRecord2(null);

        // Enable edit mode for adding
        setIsAdding(true);
        setIsEditing(true);
    };

    const deleteHandler = () => {
        if (!selectedRecord2?.id) {
            notifyError(t('common.noRecordSelected') || 'Моля изберете запис за изтриване');
            return;
        }
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedRecord2?.id) {
            notifyError(t('common.noRecordSelected') || 'Моля изберете запис за изтриване');
            setShowDeleteModal(false);
            return;
        }

        try {
            setNomstructLoading(true);
            
            // Delete the profile
            await additionalAccessService.deleteHrProfilesUsers(selectedRecord2.id);
            
            // Clear form fields
            setCalendarChecked(false);
            setNormsChecked(false);
            setFixChecked(false);
            setEditReportTemplatesChecked(false);
            setProfileName('');
            setProfileDescription('');
            setSelectedAdministerBranch(null);
            setSelectedReportsBranches([]);
            setProfileDetails(null);
            setSelectedRecord2(null);
            setIsEditing(false);
            setIsAdding(false);
            
            // Refresh the access rights table data
            if (selectedUserRecord?.id) {
                const profilesData = await additionalAccessService.getHrProfilesUsersByUserId(selectedUserRecord.id);
                const mappedProfiles = Array.isArray(profilesData) ? profilesData.map(profile => ({
                    id: profile.id,
                    activeProfile: profile.default,
                    titleProfile: profile.shortDescription,
                    descriptionProfile: profile.longDescription
                })) : [];
                setAccessRightsData(mappedProfiles);
            }
            
            setShowDeleteModal(false);
            notifySuccess(t('common.deletedSuccessfully') || 'Успешно изтрито');
        } catch (error) {
            console.error('Error deleting profile:', error);
            const errorMessage = error?.response?.data?.message || t('common.deleteError') || 'Грешка при изтриване';
            notifyError(errorMessage);
        } finally {
            setNomstructLoading(false);
        }
    };

    // Convert 1-based pageNumber to 0-based for Table/Pagination component
    // and convert back when pageNumber changes
    const handlePageNumberChange = (newPageNumber) => {
        // newPageNumber is 0-based from Pagination component
        setPageNumber(newPageNumber + 1); // Convert to 1-based for our state
    };

    return (
        <>
            <div className="large-table width-30 me-2">
                <Table
                    // title={t('additionalSettings.additionalAccess.title')}
                    columns={usersColumns}
                    data={personsData}
                    loading={loading}
                    showPageSizeSelector={false}
                    showPagination={true}
                    showCustomSearch={false}
                    showActiveSelect={true}
                    selectedActive={selectedActive}
                    setSelectedActive={setSelectedActive}
                    searchOptions={searchOptions}
                    actionButtons={false}
                    // showCheckbox={true}
                    handleCheckboxChange={handlePersonCheckboxChange}
                    selectedData={selectedUserRecord?.id}
                    pageNumber={pageNumber - 1}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    pageSize={pageSize}
                    setpageNumber={handlePageNumberChange}
                    setPageSize={setPageSize}
                    handleSearchClick={handleSearchClick}
                    getData={personService.getExtendedAccessPersons}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchType={searchType}
                    setSearchType={setSearchType}
                    handleSort={handleSort}
                  
                />
            </div>
            <div className="small-table width-70 ms-2">
                <Table
                    title={t('additionalSettings.additionalAccess.title')}
                    columns={accesRightsColumns}
                    data={accessRightsData}
                    loading={nomstructLoading}
                    showPageSizeSelector={false}
                    showPagination={false}
                    showPreviousPage={false}
                    showCustomSearch={true}
                    actionButtons={actionButtons}
                    // showCheckbox={true}
                    handleCheckboxChange={handleAccessRightsCheckboxChange}
                    selectedData={selectedRecord2?.id}
                />
                <div className='flex-column' style={{ marginTop: '10px', paddingLeft: '30px', gap: '10px' }}>
                    {/* Rights checkboxes in 2 columns x 2 rows */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isEditing ? 'pointer' : 'not-allowed', fontSize: '16px' }}>
                            <input 
                                type="checkbox" 
                                checked={calendarChecked}
                                disabled={!isEditing}
                                onChange={(e) => setCalendarChecked(e.target.checked)}
                            />
                            <span>{t('additionalSettings.additionalAccess.table.headers.calendarSettings')}</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isEditing ? 'pointer' : 'not-allowed', fontSize: '16px' }}>
                            <input 
                                type="checkbox" 
                                checked={normsChecked}
                                disabled={!isEditing}
                                onChange={(e) => setNormsChecked(e.target.checked)}
                            />
                            <span>{t('additionalSettings.additionalAccess.table.headers.normativeDocumentsSettings')}</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isEditing ? 'pointer' : 'not-allowed', fontSize: '16px' }}>
                            <input 
                                type="checkbox" 
                                checked={fixChecked}
                                disabled={!isEditing}
                                onChange={(e) => setFixChecked(e.target.checked)}
                            />
                            <span>{t('additionalSettings.additionalAccess.table.headers.fixPeriodsForAllCompanies')}</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isEditing ? 'pointer' : 'not-allowed', fontSize: '16px' }}>
                            <input
                                type="checkbox"
                                checked={editReportTemplatesChecked}
                                disabled={!isEditing}
                                onChange={(e) => setEditReportTemplatesChecked(e.target.checked)}
                            />
                            <span>{t('additionalSettings.additionalAccess.table.headers.editReportTemplates')}</span>
                        </label>
                    </div>

                    {/* Profile information fields */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ minWidth: '160px', fontSize: '16px' }}>{t('additionalSettings.additionalAccess.table.headers.profileName')}:</label>
                            <input 
                                type="text" 
                                value={profileName}
                                disabled={!isAdding}
                                onChange={(e) => setProfileName(e.target.value)}
                                style={{ 
                                    flex: 1, 
                                    padding: '4px 8px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px', 
                                    fontSize: '13px', 
                                    backgroundColor: isAdding ? 'white' : '#f5f5f5', 
                                    cursor: isAdding ? 'text' : 'not-allowed' 
                                }} 
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ minWidth: '200px', fontSize: '16px' }}>{t('additionalSettings.additionalAccess.table.headers.profileDescription')}:</label>
                            <input 
                                type="text" 
                                value={profileDescription}
                                disabled={!isAdding}
                                onChange={(e) => setProfileDescription(e.target.value)}
                                style={{ 
                                    flex: 1, 
                                    padding: '4px 8px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px', 
                                    fontSize: '13px', 
                                    backgroundColor: isAdding ? 'white' : '#f5f5f5', 
                                    cursor: isAdding ? 'text' : 'not-allowed' 
                                }} 
                            />
                        </div>
                    </div>

                    {/* Two columnar sections for permissions */}
                    <div style={{ display: 'flex', gap: '12px', marginTop: '5px' }}>
                        {/* Left Section: Администрира */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <h4 style={{ margin: 0, fontWeight: '600', fontSize: '16px' }}>{t('additionalSettings.additionalAccess.table.headers.administrate')}</h4>
                            {/* Spacer div to align with button row in second section */}
                            <div style={{ height: '30px' }}></div>
                            <div style={{ backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px', display: 'flex', flexDirection: 'column', gap: '6px', minHeight: '150px' }}>
                                {branchesLoading ? (
                                    <div style={{ padding: '8px', fontSize: '16px', textAlign: 'center' }}>Loading...</div>
                                ) : branchesData.length > 0 ? (
                                    branchesData.map((branch) => (
                                        <label key={branch.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isEditing ? 'pointer' : 'not-allowed', fontSize: '16px' }}>
                                            <input
                                                type="checkbox"
                                                value={branch.id}
                                                checked={selectedAdministerBranch === branch.id}
                                                disabled={!isEditing}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedAdministerBranch(branch.id);
                                                    } else {
                                                        setSelectedAdministerBranch(null);
                                                    }
                                                }}
                                            />
                                            <span>{branch.name}</span>
                                        </label>
                                    ))
                                ) : (
                                    <div style={{ padding: '8px', fontSize: '16px', textAlign: 'center' }}>No branches available</div>
                                )}
                            </div>
                        </div>

                        {/* Right Section: Формиране на справки за */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <h4 style={{ margin: 0, fontWeight: '600', fontSize: '16px' }}>{t('additionalSettings.additionalAccess.table.headers.formReports')}</h4>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <button 
                                    onClick={() => {
                                        if (isEditing) {
                                            const allBranchIds = branchesData.map(branch => branch.id);
                                            setSelectedReportsBranches(allBranchIds);
                                        }
                                    }}
                                    disabled={!isEditing}
                                    style={{ 
                                        padding: '4px 10px', 
                                        backgroundColor: isEditing ? '#007bff' : '#6c757d', 
                                        color: 'white', 
                                        border: 'none', 
                                        borderRadius: '4px', 
                                        cursor: isEditing ? 'pointer' : 'not-allowed', 
                                        fontSize: '16px',
                                        opacity: isEditing ? 1 : 0.6
                                    }}
                                >
                                    {t('additionalSettings.additionalAccess.table.headers.chooseAllBranches')}
                                </button>
                                <button 
                                    onClick={() => {
                                        if (isEditing) {
                                            setSelectedReportsBranches([]);
                                        }
                                    }}
                                    disabled={!isEditing}
                                    style={{ 
                                        padding: '4px 10px', 
                                        backgroundColor: isEditing ? '#6c757d' : '#9e9e9e', 
                                        color: 'white', 
                                        border: 'none', 
                                        borderRadius: '4px', 
                                        cursor: isEditing ? 'pointer' : 'not-allowed', 
                                        fontSize: '16px',
                                        opacity: isEditing ? 1 : 0.6
                                    }}
                                >
                                    {t('additionalSettings.additionalAccess.table.headers.cancelAllBranches')}
                                </button>
                            </div>
                            <div style={{ backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px', display: 'flex', flexDirection: 'column', gap: '6px', minHeight: '150px' }}>
                                {branchesLoading ? (
                                    <div style={{ padding: '8px', fontSize: '16px', textAlign: 'center' }}>Loading...</div>
                                ) : branchesData.length > 0 ? (
                                    branchesData.map((branch) => (
                                        <label key={branch.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isEditing ? 'pointer' : 'not-allowed', fontSize: '16px' }}>
                                            <input
                                                type="checkbox"
                                                value={branch.id}
                                                checked={selectedReportsBranches.includes(branch.id)}
                                                disabled={!isEditing}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedReportsBranches([...selectedReportsBranches, branch.id]);
                                                    } else {
                                                        setSelectedReportsBranches(selectedReportsBranches.filter(id => id !== branch.id));
                                                    }
                                                }}
                                            />
                                            <span>{branch.name}</span>
                                        </label>
                                    ))
                                ) : (
                                    <div style={{ padding: '8px', fontSize: '16px', textAlign: 'center' }}>No branches available</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showDeleteModal && (
                <DeleteModal
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDeleteConfirm}
                    name={selectedRecord2?.titleProfile || profileDetails?.shortDescription || t('common.record')}
                    entityType="record"
                />
            )}
        </>
    );

}